import type { CitationPreviewState } from "./citation-preview-shared"
import type { Document } from "@/api/models"

import { CitationSidebarHeader } from "@/features/chat/components/citation-sidebar-header"
import { CitationViewerContent } from "@/features/chat/components/citation-viewer-content"
import i18n from "@/languageConfig"
import {
  type ContentSidebarHeaderContext,
  useContentSidebarStore,
} from "@/stores/content-sidebar-store"

import { resolveCitationPreview } from "./citation-preview-utils"

type CitationPreviewSidebarOptions = {
  chunkText?: string
  documents?: [string, Document][]
  backLabel?: string
  preserveSidebarStack?: boolean
}

type OpenCitationPreviewPanelOptions = {
  citation: string
  citationSection: string
  options?: CitationPreviewSidebarOptions
  onClose?: () => void
}

export function openCitationPreviewPanel({
  citation,
  citationSection,
  options,
  onClose,
}: OpenCitationPreviewPanelOptions): CitationPreviewState {
  const {
    state: nextCitation,
    title: citationTitle,
    downloadFileName,
    downloadKind,
    sourceRevision,
  } = resolveCitationPreview(citation, citationSection, options)
  const onBack = options?.preserveSidebarStack
    ? () => useContentSidebarStore.getState().popPanel()
    : undefined

  const panel = {
    id: "citation-preview",
    title: citationTitle || i18n.t("analysispanel.citation", { defaultValue: "Cited document" }),
    headerMode: "custom" as const,
    renderHeader: (context: ContentSidebarHeaderContext) => (
      <CitationSidebarHeader
        backLabel={options?.backLabel}
        closeable={context.closeable}
        downloadFileName={downloadFileName}
        downloadKind={downloadKind}
        downloadUrl={nextCitation.downloadUrl}
        isFocusable={context.isFocusable}
        isFocused={context.isFocused}
        sourceRevision={sourceRevision}
        title={citationTitle}
        onBack={onBack}
        onClose={context.onClose}
        onToggleFocus={context.onToggleFocus}
      />
    ),
    content: (
      <CitationViewerContent
        chunkSnippet={nextCitation.chunkSnippet}
        documentUrl={nextCitation.documentUrl}
        isUnresolvedSource={nextCitation.isUnresolvedSource}
        originalDocumentUrl={nextCitation.originalDocumentUrl}
        quote={nextCitation.quote}
        quoteSnippet={nextCitation.quoteSnippet}
        showModifiedPreviewBanner={nextCitation.showModifiedPreviewBanner}
        textAnchor={nextCitation.textAnchor}
      />
    ),
    focusable: true,
    closeable: true,
    resizable: true,
    isDirty: () => false,
    onClose,
  }

  const sidebarStore = useContentSidebarStore.getState()
  if (options?.preserveSidebarStack) {
    sidebarStore.pushPanel(panel)
  } else {
    sidebarStore.openPanel(panel)
  }

  return nextCitation
}
